import React, { useEffect, useMemo, useState, createElement } from 'react';
// Reporte modernizado automáticamente con colores Junquito Gas
import { TARIFA } from '../data';
import type { AppState, Consumo } from '../types';
import { PageShell, InfoCard, SectionTitle, MotionGrid, MotionItem, glassLight, inputStyle, primaryButton, ghostButton, EmptyState, useIsMobile, usePagination, Pagination, T } from '../ui';
const money = (n: number) => 'RD$ ' + Math.round(n).toLocaleString('es-DO');
const kwhFmt = (n: number) => Math.round(n).toLocaleString('es-DO') + ' kWh';
const dateLabel = (ts: number) => new Date(ts).toLocaleDateString('es-DO', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});
const LOGO_URL = "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/3naxhgyiiyi6ejjvg5a7v7/c0703513-c61f-40e6-9145-0ad9ffbfbb42/pasted-image.png";
const xml = (v: string | number) => String(v).split('&').join('&amp;').split('<').join('&lt;').split('>').join('&gt;').split('"').join('&quot;');
function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
interface ReportRow {
  sucursalId: string;
  sucursal: string;
  fecha: number;
  kwh: number;
  costo: number;
}
// Consumo de cada lectura = lectura actual − lectura anterior (cronológico) por sucursal.
function buildRows(consumos: Consumo[], nombreDe: (id: string) => string): ReportRow[] {
  const porSuc = new Map<string, Consumo[]>();
  consumos.forEach(c => {
    const arr = porSuc.get(c.sucursalId) ?? [];
    arr.push(c);
    porSuc.set(c.sucursalId, arr);
  });
  const rows: ReportRow[] = [];
  porSuc.forEach((arr, sucId) => {
    const asc = [...arr].sort((a, b) => a.fecha - b.fecha);
    asc.forEach((c, i) => {
      if (i === 0) return;
      const kwh = Math.max(0, c.kwh - asc[i - 1].kwh);
      rows.push({
        sucursalId: sucId,
        sucursal: nombreDe(sucId),
        fecha: c.fecha,
        kwh,
        costo: kwh * TARIFA
      });
    });
  });
  return rows.sort((a, b) => b.fecha - a.fecha);
}
// Hoja de estilos del PDF como cadena plana. Colores Junquito Gas: Rojo/Naranja + Amarillo
const PDF_CSS = ['@page{margin:28px;size:A4;}', 'html,body{margin:0;padding:0;}', '*{box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif !important;-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;}', 'body{color:#0f172a;background:#f8fafc;font-size:12px;line-height:1.55;}', '.head{display:flex;align-items:center;gap:16px;padding:18px 22px;background:linear-gradient(135deg,#FF4500,#FF6B35);border-radius:14px;color:#ffffff;margin-bottom:20px;border-bottom:4px solid #FFD60A;}', '.head .logo{height:42px;width:42px;border-radius:10px;background:#ffffff;display:flex;align-items:center;justify-content:center;overflow:hidden;flex:0 0 auto;}', '.head .logo img{height:34px;width:34px;object-fit:contain;}', '.head .brand{flex:1;}', '.head .brand h1{font-size:18px;margin:0;font-weight:800;letter-spacing:-.01em;color:#ffffff;}', '.head .brand .org{font-size:11px;color:#FFD60A;margin-top:3px;font-weight:600;}', '.head .meta{text-align:right;font-size:10px;line-height:1.6;color:#fffbea;}', '.head .meta b{display:block;font-size:9px;text-transform:uppercase;letter-spacing:.08em;color:#FFD60A;font-weight:700;}', '.head .meta span{color:#ffffff;font-weight:600;}', '.cards{display:flex;gap:12px;margin-bottom:18px;}', '.card{flex:1;border:1.5px solid #e2e8f0;border-radius:11px;padding:13px 15px;background:#f8fafc;}', '.card .lbl{font-size:9px;letter-spacing:.07em;text-transform:uppercase;color:#64748b;font-weight:700;}', '.card .val{font-size:20px;font-weight:800;margin-top:5px;color:#0f172a;letter-spacing:-.02em;}', '.card.accent{background:#FFF4E6;border-color:#FF4500;}', '.card.accent .val{color:#FF4500;}', 'table{width:100%;border-collapse:collapse;font-size:11.5px;border:1.5px solid #cbd5e1;}', 'thead th{text-align:left;background:linear-gradient(135deg,#FF4500,#FF6B35);color:#ffffff;padding:9px 12px;font-size:9.5px;text-transform:uppercase;letter-spacing:.05em;font-weight:700;border-bottom:3px solid #FFD60A;}', 'thead th.num{text-align:right;}', 'tbody td{padding:8px 12px;border-bottom:1px solid #e9eef5;}', 'tbody tr:nth-child(even){background:#FFF4E6;}', 'td.num{text-align:right;}', 'td.suc{font-weight:600;color:#0f172a;}', 'td.cost{color:#FF4500;font-weight:700;}', 'tfoot td{font-weight:800;border-top:2px solid #FFD60A;background:#FFF4E6;padding:10px 12px;color:#0f172a;}', 'tfoot td.cost{color:#FF4500;}', '.foot{margin-top:22px;padding-top:12px;border-top:1px solid #e2e8f0;text-align:center;font-size:9px;color:#94a3b8;}'].join('');
const PDF_HTML_CSS = ['@page{margin:24px;size:A4;}', '*{box-sizing:border-box;-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;}', 'body{font-family:Arial,Helvetica,sans-serif;background:#ffffff;color:#1e293b;padding:8px;font-size:12px;}', '.header{display:flex;align-items:center;justify-content:space-between;background:#F8F1E5;color:#3B2F2F;padding:20px 24px;border-radius:16px;margin-bottom:22px;border-bottom:4px solid #D4A017;}', '.header .logo{display:flex;align-items:center;gap:14px;}', '.header .logo .badge{width:48px;height:48px;border-radius:12px;background:#ffffff;display:flex;align-items:center;justify-content:center;overflow:hidden;flex:0 0 auto;}', '.header .logo .badge img{width:40px;height:40px;object-fit:contain;display:block;}', '.header .logo img{height:72px;width:auto;max-width:300px;object-fit:contain;display:block;}', '.header .titulo{font-size:20px;font-weight:800;letter-spacing:-.01em;}', '.header .sub{font-size:12px;color:#FFD60A;margin-top:3px;font-weight:600;}', '.header .gen{text-align:right;font-size:10px;color:#fffbea;}', '.header .gen b{display:block;font-size:9px;text-transform:uppercase;letter-spacing:.08em;color:#FFD60A;}', '.cards{display:flex;gap:14px;margin-bottom:20px;}', '.card{flex:1;background:#f8fafc;border:1.5px solid #e2e8f0;border-radius:13px;padding:15px 17px;}', '.card h4{margin:0;font-size:9px;letter-spacing:.07em;text-transform:uppercase;color:#64748b;font-weight:700;}', '.card h2{margin:7px 0 0;font-size:21px;color:#0f172a;font-weight:800;letter-spacing:-.02em;}', '.card.accent{background:#FFF4E6;border-color:#FF4500;}', '.card.accent h2{color:#FF4500;}', '.chart{background:#f8fafc;border:1.5px solid #e2e8f0;border-radius:14px;padding:16px 18px;margin-bottom:20px;}', '.chart-title{font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:#64748b;font-weight:700;margin-bottom:10px;}', 'table{width:100%;border-collapse:collapse;font-size:11.5px;border:1.5px solid #cbd5e1;table-layout:fixed;}', 'col.c-suc{width:32%;}col.c-fecha{width:22%;}col.c-kwh{width:23%;}col.c-cost{width:23%;}', 'thead th{background:linear-gradient(135deg,#FF4500,#FF6B35);color:#ffffff;text-align:left;padding:10px 13px;font-size:9.5px;text-transform:uppercase;letter-spacing:.05em;font-weight:700;border-bottom:3px solid #FFD60A;overflow:hidden;text-overflow:ellipsis;}', 'thead th.num{text-align:right;}', 'tbody td{padding:9px 13px;border-bottom:1px solid #eef2f7;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}', 'tbody td.num{text-align:right;}', 'tbody td.cost{color:#FF4500;font-weight:700;text-align:right;}', 'tbody tr:nth-child(even){background:#FFF4E6;}', 'tfoot td{font-weight:800;border-top:2px solid #FFD60A;background:#FFF4E6;padding:11px 13px;}', 'tfoot td.num{text-align:right;}', 'tfoot td.cost{color:#FF4500;text-align:right;}', '.footer{margin-top:22px;padding-top:12px;border-top:1px solid #e2e8f0;text-align:center;color:#94a3b8;font-size:10px;}'].join('');
export function Reportes({
  state,
  onLog
}: {
  state: AppState;
  onLog?: (msg: string) => void;
}) {
  const isMobile = useIsMobile();
  const nombreDe = (id: string) => state.sucs.find(s => s.id === id)?.nombre ?? id;
  const hoy = new Date();
  const hace6 = new Date(hoy.getFullYear(), hoy.getMonth() - 6, 1);
  const [desde, setDesde] = useState(hace6.toISOString().slice(0, 10));
  const [hasta, setHasta] = useState(hoy.toISOString().slice(0, 10));
  // Sucursales seleccionadas para el reporte (una o varias). Por defecto todas.
  const allSucIds = useMemo(() => state.sucs.map(s => s.id), [state.sucs]);
  const [selSucs, setSelSucs] = useState<string[]>(allSucIds);
  // Mantener la selección consistente si cambian las sucursales disponibles.
  useEffect(() => {
    setSelSucs(prev => {
      const validas = prev.filter(id => allSucIds.includes(id));
      return validas.length ? validas : allSucIds;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSucIds.join(',')]);
  const todasSeleccionadas = allSucIds.length > 0 && selSucs.length === allSucIds.length;
  const toggleSuc = (id: string) => setSelSucs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const seleccionarTodas = () => setSelSucs(allSucIds);
  const allRows = useMemo(() => buildRows(state.consumos, nombreDe), [state.consumos, state.sucs]);
  const rows = useMemo(() => {
    const desdeTs = desde ? new Date(desde + 'T00:00:00').getTime() : -Infinity;
    const hastaTs = hasta ? new Date(hasta + 'T23:59:59').getTime() : Infinity;
    const sel = new Set(selSucs);
    return allRows.filter(r => r.fecha >= desdeTs && r.fecha <= hastaTs && (sel.size === 0 || sel.has(r.sucursalId)));
  }, [allRows, desde, hasta, selSucs]);
  const totalKwh = rows.reduce((a, r) => a + r.kwh, 0);
  const totalCosto = rows.reduce((a, r) => a + r.costo, 0);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(rows, 8);
  const rangoTexto = (desde ? dateLabel(new Date(desde + 'T00:00:00').getTime()) : 'inicio') + ' — ' + (hasta ? dateLabel(new Date(hasta + 'T00:00:00').getTime()) : 'hoy');
  const exportExcel = () => {
    const Q = String.fromCharCode(38) + 'quot;';
    const moneyFmt = Q + 'RD$' + Q + ' #,##0';
    const cell = (v: string | number, styleId?: string, mergeAcross?: number) => '<Cell' + (styleId ? ' ss:StyleID="' + styleId + '"' : '') + (mergeAcross ? ' ss:MergeAcross="' + mergeAcross + '"' : '') + '><Data ss:Type="' + (typeof v === 'number' ? 'Number' : 'String') + '">' + xml(v) + '</Data></Cell>';
    const row = (cells: string, height?: number) => '<Row' + (height ? ' ss:Height="' + height + '"' : '') + '>' + cells + '</Row>';
    const titleRow = row(cell('GETR · Reporte de Consumos Energeticos', 'sTitle', 3), 30);
    const subRow = row(cell('Grupo Economico Torres Rodriguez · Rango: ' + rangoTexto + ' · Generado: ' + new Date().toLocaleString('es-DO'), 'sSub', 3), 20);
    const blankRow = row(cell('', 'sBlank', 3));
    const headerRow = row(['Sucursal', 'Fecha', 'Consumo (kWh)', 'Costo (RD$)'].map(h => cell(h, 'sHead')).join(''));
    const bodyRows = rows.map(r => row(cell(r.sucursal, 'sCell') + cell(dateLabel(r.fecha), 'sCell') + cell(Math.round(r.kwh), 'sNum') + cell(Math.round(r.costo), 'sCost'))).join('');
    const totalRow = row(cell('TOTAL', 'sTotal') + cell('', 'sTotal') + cell(Math.round(totalKwh), 'sTotalNum') + cell(Math.round(totalCosto), 'sTotalCost'));
    const styles = '<Styles>' + '<Style ss:ID="sTitle"><Font ss:Bold="1" ss:Size="16" ss:Color="#FFFFFF"/><Interior ss:Color="#FF4500" ss:Pattern="Solid"/><Alignment ss:Vertical="Center"/></Style>' + '<Style ss:ID="sSub"><Font ss:Size="9" ss:Color="#FFFFFF"/><Interior ss:Color="#FF6B35" ss:Pattern="Solid"/><Alignment ss:Vertical="Center"/></Style>' + '<Style ss:ID="sBlank"/>' + '<Style ss:ID="sHead"><Font ss:Bold="1" ss:Size="10" ss:Color="#FFFFFF"/><Interior ss:Color="#FF4500" ss:Pattern="Solid"/><Alignment ss:Horizontal="Left" ss:Vertical="Center"/></Style>' + '<Style ss:ID="sCell"><Borders><Border ss:Position="Bottom" ss:Color="#EEF2F7" ss:Weight="1" ss:LineStyle="Continuous"/></Borders></Style>' + '<Style ss:ID="sNum"><NumberFormat ss:Format="#,##0"/><Alignment ss:Horizontal="Right"/><Borders><Border ss:Position="Bottom" ss:Color="#EEF2F7" ss:Weight="1" ss:LineStyle="Continuous"/></Borders></Style>' + '<Style ss:ID="sCost"><Font ss:Bold="1" ss:Color="#FF4500"/><NumberFormat ss:Format="' + moneyFmt + '"/><Alignment ss:Horizontal="Right"/><Borders><Border ss:Position="Bottom" ss:Color="#EEF2F7" ss:Weight="1" ss:LineStyle="Continuous"/></Borders></Style>' + '<Style ss:ID="sTotal"><Font ss:Bold="1"/><Interior ss:Color="#FFF4E6" ss:Pattern="Solid"/><Borders><Border ss:Position="Top" ss:Color="#FF4500" ss:Weight="2" ss:LineStyle="Continuous"/></Borders></Style>' + '<Style ss:ID="sTotalNum"><Font ss:Bold="1"/><NumberFormat ss:Format="#,##0"/><Alignment ss:Horizontal="Right"/><Interior ss:Color="#FFF4E6" ss:Pattern="Solid"/><Borders><Border ss:Position="Top" ss:Color="#FF4500" ss:Weight="2" ss:LineStyle="Continuous"/></Borders></Style>' + '<Style ss:ID="sTotalCost"><Font ss:Bold="1" ss:Color="#FF4500"/><NumberFormat ss:Format="' + moneyFmt + '"/><Alignment ss:Horizontal="Right"/><Interior ss:Color="#FFF4E6" ss:Pattern="Solid"/><Borders><Border ss:Position="Top" ss:Color="#FF4500" ss:Weight="2" ss:LineStyle="Continuous"/></Borders></Style>' + '</Styles>';
    const columns = '<Column ss:Width="170"/><Column ss:Width="110"/><Column ss:Width="110"/><Column ss:Width="110"/>';
    const doc = '<?xml version="1.0"?><?mso-application progid="Excel.Sheet"?>' + '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">' + styles + '<Worksheet ss:Name="Consumos"><Table>' + columns + titleRow + subRow + blankRow + headerRow + bodyRows + totalRow + '</Table></Worksheet></Workbook>';
    downloadBlob('reporte-consumos-getr.xls', new Blob([doc], {
      type: 'application/vnd.ms-excel'
    }));
    onLog?.('Reporte de consumos exportado a Excel (' + rows.length + ')');
  };
  const exportPdf = () => {
    const filasPdf = rows.map(r => '<tr>' + '<td class="suc">' + xml(r.sucursal) + '</td>' + '<td>' + xml(dateLabel(r.fecha)) + '</td>' + '<td class="num">' + xml(kwhFmt(r.kwh)) + '</td>' + '<td class="cost">' + xml(money(r.costo)) + '</td>' + '</tr>').join('');
    const head = '<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Reporte GETR</title><style>' + PDF_HTML_CSS + '`}</style></head>';
    const header = '<div class="header">' + '<div class="logo">' + '<img src="' + LOGO_URL + '">' + '<div>' + '<div class="titulo">Reporte de Consumos GETR</div>' + '<div class="sub">Junquito Gas Electricidad</div>' + '</div>' + '</div>' + '<div>' + xml(new Date().toLocaleString('es-DO')) + '</div>' + '</div>';
    const cards = '<div class="cards">' + '<div class="card"><h4>Consumo Total</h4><h2>' + xml(kwhFmt(totalKwh)) + '</h2></div>' + '<div class="card"><h4>Costo Total</h4><h2>' + xml(money(totalCosto)) + '</h2></div>' + '<div class="card"><h4>Registros</h4><h2>' + rows.length + '</h2></div>' + '</div>';
    // Grafico comparativo: total de consumo (kWh) por sucursal en el rango.
    const porSuc: Record<string, number> = {};
    rows.forEach(r => {
      porSuc[r.sucursal] = (porSuc[r.sucursal] || 0) + r.kwh;
    });
    const entradas = Object.keys(porSuc).map(nombre => ({
      nombre,
      kwh: porSuc[nombre]
    }));
    const maxKwh = Math.max(1, ...entradas.map(e => e.kwh));
    const paleta = ['#FF4500', '#FFD60A', '#FF6B35', '#FFB84D', '#FF8C42', '#FFA500'];
    const chartW = 720;
    const chartH = 240;
    const padBottom = 46;
    const padTop = 26;
    const usableH = chartH - padBottom - padTop;
    const slot = chartW / Math.max(1, entradas.length);
    const barW = Math.min(90, slot * 0.55);
    const barras = entradas.map((e, i) => {
      const h = Math.round(e.kwh / maxKwh * usableH);
      const x = Math.round(i * slot + (slot - barW) / 2);
      const y = chartH - padBottom - h;
      const color = paleta[i % paleta.length];
      const cx = Math.round(i * slot + slot / 2);
      const valor = Math.round(e.kwh).toLocaleString('es-DO');
      return '<rect x="' + x + '" y="' + y + '" width="' + Math.round(barW) + '" height="' + h + '" rx="6" fill="' + color + '"></rect>' + '<text x="' + cx + '" y="' + (y - 7) + '" text-anchor="middle" font-size="12" font-weight="700" fill="#0f172a">' + xml(valor) + '</text>' + '<text x="' + cx + '" y="' + (chartH - padBottom + 20) + '" text-anchor="middle" font-size="11" font-weight="600" fill="#475569">' + xml(e.nombre) + '</text>';
    }).join('');
    const baseY = chartH - padBottom;
    const chartSvg = '<svg viewBox="0 0 ' + chartW + ' ' + chartH + '" width="100%" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">' + '<line x1="0" y1="' + baseY + '" x2="' + chartW + '" y2="' + baseY + '" stroke="#cbd5e1" stroke-width="1.5"></line>' + barras + '</svg>';
    const grafico = entradas.length === 0 ? '' : '<div class="chart">' + '<div class="chart-title">Comparacion de consumo por sucursal</div>' + chartSvg + '</div>';
    const tabla = '<table>' + '<colgroup><col class="c-suc"><col class="c-fecha"><col class="c-kwh"><col class="c-cost"></colgroup>' + '<thead><tr><th>Sucursal</th><th>Fecha</th><th class="num">Consumo</th><th class="num">Costo</th></tr></thead>' + '<tbody>' + filasPdf + '</tbody>' + '<tfoot><tr>' + '<td>TOTAL</td><td></td>' + '<td class="num">' + xml(kwhFmt(totalKwh)) + '</td>' + '<td class="cost">' + xml(money(totalCosto)) + '</td>' + '</tr></tfoot>' + '</table>';
    const footer = '<div class="footer">Documento generado automaticamente por el sistema GETR</div>';
    const html = head + '<body>' + header + cards + grafico + tabla + footer + '</body></html>';
    const ventana = window.open('', '_blank');
    if (!ventana) return;
    ventana.document.write(html);
    ventana.document.close();
    // Esperar a que el logo (imagen remota) cargue antes de imprimir, para
    // que aparezca en el encabezado del PDF.
    const imprimir = () => {
      try {
        ventana.focus();
        ventana.print();
      } catch (e) {}
    };
    const img = ventana.document.querySelector('img');
    if (img && !img.complete) {
      img.onload = () => setTimeout(imprimir, 250);
      img.onerror = () => setTimeout(imprimir, 250);
      // red de seguridad por si la imagen nunca dispara load
      setTimeout(imprimir, 2500);
    } else {
      setTimeout(imprimir, 500);
    }
    onLog?.('Reporte de consumos generado en PDF (' + rows.length + ')');
  };
  const dateField: React.CSSProperties = {
    ...inputStyle,
    colorScheme: 'dark'
  };
  const labelHint: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    color: T.textMuted,
    letterSpacing: '.08em',
    textTransform: 'uppercase',
    marginBottom: 7
  };
  return <PageShell title="Reportes de consumo" sub="Filtra por rango de fechas y exporta a Excel o PDF" data-id="element-978" data-mp="3eh8svmtso9vk">
      
      <div style={{
      ...glassLight,
      padding: isMobile ? 16 : 20
    }} data-id="element-979" data-mp="3gzvl8h7p6dgo">
        
        <SectionTitle data-id="element-980" data-mp="3lyyphzfusfnl">Rango de fechas</SectionTitle>
        <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 14,
        alignItems: isMobile ? 'stretch' : 'flex-end'
      }} data-id="element-981" data-mp="3lzqayhfqkx05">
          
          <label style={{
          flex: 1,
          minWidth: 0
        }} data-id="element-982" data-mp="3vy64er28kict">
            
            <div style={labelHint} data-id="element-983" data-mp="3salkcwjhyji">Desde</div>
            <input type="date" value={desde} max={hasta || undefined} onChange={e => setDesde(e.target.value)} style={dateField} data-id="element-984" data-mp="3r967w8hqpvz" />
            
          </label>
          <label style={{
          flex: 1,
          minWidth: 0
        }} data-id="element-985" data-mp="dkgateveq1v0">
            
            <div style={labelHint} data-id="element-986" data-mp="ih6ra3b4izgy">Hasta</div>
            <input type="date" value={hasta} min={desde || undefined} onChange={e => setHasta(e.target.value)} style={dateField} data-id="element-987" data-mp="ik2p7rmz1o5e" />
            
          </label>
        </div>

        <div style={{
        marginTop: 18
      }} data-id="element-988" data-mp="rjg2ywi4oyvx">
          
          <div style={labelHint} data-id="element-989" data-mp="1qztkosw487xw">Sucursales</div>
          <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8
        }} data-id="element-990" data-mp="1r14outth3hpj">
            
            <button type="button" onClick={seleccionarTodas} style={{
            padding: '7px 14px',
            borderRadius: 999,
            border: `1px solid ${todasSeleccionadas ? T.accent : T.border}`,
            background: todasSeleccionadas ? T.accentSoft : 'transparent',
            color: todasSeleccionadas ? T.accent : T.textSecondary,
            fontSize: 12.5,
            fontWeight: 700,
            whiteSpace: 'nowrap'
          }} data-id="element-991" data-mp="20x7qlykexeby">
              
              Todas
            </button>
            {state.sucs.map(s => {
            const activa = selSucs.includes(s.id);
            return <button key={s.id} type="button" onClick={() => toggleSuc(s.id)} style={{
              padding: '7px 14px',
              borderRadius: 999,
              border: `1px solid ${activa ? T.accent : T.border}`,
              background: activa ? T.accentSoft : 'transparent',
              color: activa ? T.accent : T.textSecondary,
              fontSize: 12.5,
              fontWeight: activa ? 700 : 600,
              whiteSpace: 'nowrap'
            }} data-id="element-992" data-mp="2sonkmdoau3c7">
                  
                  {s.nombre}
                </button>;
          })}
          </div>
          {selSucs.length === 0 && <div style={{
          fontSize: 12,
          color: T.warn,
          marginTop: 8
        }} data-id="element-993" data-mp="14qd094zxlsi6">
            
              Selecciona al menos una sucursal para ver datos.
            </div>}
        </div>
      </div>

      <MotionGrid style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))',
      gap: 14
    }} data-id="element-994" data-mp="vx9g6dus6hek">
        
        <MotionItem data-id="element-995" data-mp="qxdlj57z42bo">
          <InfoCard title="Consumo total" value={kwhFmt(totalKwh)} note="Suma del rango seleccionado." data-id="element-996" data-mp="1oec2tskcdtci" />
          
        </MotionItem>
        <MotionItem data-id="element-997" data-mp="1jn3r2pvjhvw6">
          <InfoCard title="Costo estimado" value={money(totalCosto)} note="Segun la tarifa interna del sistema." data-id="element-998" data-mp="1jmkku6f94vxz" />
          
        </MotionItem>
        <MotionItem data-id="element-999" data-mp="27jqlx20frdxu">
          <InfoCard title="Registros" value={String(rows.length)} note="Lecturas dentro del rango." data-id="element-1000" data-mp="27i5rdce4fuzz" />
          
        </MotionItem>
      </MotionGrid>

      <div data-id="element-1001" data-mp="1tpzjedl4gubm">
        <SectionTitle data-id="element-1002" data-mp="1xt5pnl88ili3">Exportar</SectionTitle>
        <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 10
      }} data-id="element-1003" data-mp="1xs2xl9z6ed0l">
          
          <button onClick={exportExcel} disabled={rows.length === 0} style={{
          ...primaryButton,
          flex: isMobile ? 'none' : 1,
          opacity: rows.length === 0 ? 0.5 : 1,
          cursor: rows.length === 0 ? 'not-allowed' : 'pointer'
        }} data-id="element-1004" data-mp="1jvz8tw72mwvi">
            
            Exportar Excel (.xls)
          </button>
          <button onClick={exportPdf} disabled={rows.length === 0} style={{
          ...ghostButton,
          padding: '10px 20px',
          flex: isMobile ? 'none' : 1,
          opacity: rows.length === 0 ? 0.5 : 1,
          cursor: rows.length === 0 ? 'not-allowed' : 'pointer'
        }} data-id="element-1005" data-mp="356pkxjeloxyw">
            
            Generar PDF
          </button>
        </div>
      </div>

      <div data-id="element-1006" data-mp="17aqw2t2ts19i">
        <SectionTitle data-id="element-1007" data-mp="17bk8a9kjziz1">Detalle de consumos</SectionTitle>
        {rows.length === 0 ? <EmptyState title="Sin consumos en el rango" hint="Ajusta el rango de fechas o registra lecturas en el modulo de Consumo." data-id="element-1008" data-mp="12kajtcaqqloq" /> : <div style={{
        ...glassLight,
        overflow: 'hidden'
      }} data-id="element-1009" data-mp="1h8f42kpfz59m">
          
            <div style={{
          overflowX: 'auto'
        }} data-id="element-1010" data-mp="1can3l2aqeatt">
            
              <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 13,
            minWidth: 460
          }} data-id="element-1011" data-mp="1q9mopzz1c7l8">
              
                <thead data-id="element-1012" data-mp="1l8yznjuda0tn">
                  <tr style={{
                background: T.surfaceSubtle
              }} data-id="element-1013" data-mp="3s0zpqdxvtvc0">
                  
                    {['Sucursal', 'Fecha', 'Consumo', 'Costo'].map((h, i) => <th key={h} style={{
                  padding: '13px 16px',
                  fontWeight: 700,
                  fontSize: 10,
                  letterSpacing: '.1em',
                  textTransform: 'uppercase',
                  color: T.textMuted,
                  textAlign: i >= 2 ? 'right' : 'left',
                  borderBottom: `1px solid ${T.border}`,
                  whiteSpace: 'nowrap'
                }} data-id="element-1014" data-mp="3n49fw8co7vz1" data-mpi={i}>
                    
                        {h}
                      </th>)}
                  </tr>
                </thead>
                <tbody data-id="element-1015" data-mp="3asp4n6pv61es">
                  {pageItems.map((r, i) => <tr key={`${r.sucursal}-${r.fecha}-${i}`} className="getr-row" style={{
                background: i % 2 === 1 ? 'rgba(255,255,255,.015)' : 'transparent',
                borderBottom: i < pageItems.length - 1 ? `1px solid ${T.border}` : 'none',
                transition: 'background .15s'
              }} data-id="element-1016" data-mp="3fr0evik2mdxj" data-mpi={i}>
                  
                      <td style={{
                  padding: '12px 16px',
                  fontWeight: 600,
                  color: T.text,
                  whiteSpace: 'nowrap'
                }} data-id="element-1017" data-mp="2s04ngmjj1kqt">
                    
                        {r.sucursal}
                      </td>
                      <td style={{
                  padding: '12px 16px',
                  color: T.textSecondary,
                  whiteSpace: 'nowrap'
                }} data-id="element-1018" data-mp="2aiphmr7zh84a">
                    
                        {dateLabel(r.fecha)}
                      </td>
                      <td style={{
                  padding: '12px 16px',
                  color: T.text,
                  textAlign: 'right',
                  whiteSpace: 'nowrap'
                }} data-id="element-1019" data-mp="1wslyi94ehuj2">
                    
                        {kwhFmt(r.kwh)}
                      </td>
                      <td style={{
                  padding: '12px 16px',
                  color: T.accent,
                  fontWeight: 700,
                  textAlign: 'right',
                  whiteSpace: 'nowrap'
                }} data-id="element-1020" data-mp="1hutm3pu5hm93">
                    
                        {money(r.costo)}
                      </td>
                    </tr>)}
                </tbody>
                <tfoot data-id="element-1021" data-mp="18534972dlnk0">
                  <tr style={{
                background: T.surfaceSubtle
              }} data-id="element-1022" data-mp="u7y2vkdlxcv3">
                  
                    <td style={{
                  padding: '13px 16px',
                  fontWeight: 800,
                  color: T.text
                }} data-id="element-1023" data-mp="z5zke0vdx44g">
                    
                      TOTAL
                    </td>
                    <td data-id="element-1024" data-mp="1qxfhwpyhcb9g" />
                    <td style={{
                  padding: '13px 16px',
                  fontWeight: 800,
                  color: T.text,
                  textAlign: 'right',
                  whiteSpace: 'nowrap'
                }} data-id="element-1025" data-mp="1qzrwqu8xy5ia">
                    
                      {kwhFmt(totalKwh)}
                    </td>
                    <td style={{
                  padding: '13px 16px',
                  fontWeight: 800,
                  color: T.accent,
                  textAlign: 'right',
                  whiteSpace: 'nowrap'
                }} data-id="element-1026" data-mp="2aloav27ukla0">
                    
                      {money(totalCosto)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div style={{
          padding: '4px 16px 14px'
        }} data-id="element-1027" data-mp="2or6o06voo4d5">
            
              <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="lecturas" data-id="element-1028" data-mp="211m6ztj3bh6i" />
            
            </div>
          </div>}
      </div>
    </PageShell>;
}